import React from "react"

import { surfaceColor } from "../../constants/surface-tokens"

/**
 * One compact outline glyph per category, on its own tinted tile.
 *
 * Severity decides the tint, the category decides the glyph: an overdue
 * invoice and an overdue milestone are both amber, and the silhouette is what
 * tells the reader which of them they are looking at.
 */

type Tone = "neutral" | "warning" | "critical" | "success" | "brand"

const TINT: Record<Tone, { bg: string; fg: string }> = {
  neutral: { bg: surfaceColor.chipBg, fg: surfaceColor.chipFg },
  brand: { bg: surfaceColor.tealPale, fg: surfaceColor.teal },
  warning: { bg: surfaceColor.warningBg, fg: surfaceColor.atRisk },
  critical: { bg: surfaceColor.criticalBg, fg: surfaceColor.delayedInk },
  success: { bg: surfaceColor.successBg, fg: surfaceColor.onTrack }
}

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const
}

const Glyphs: Record<string, React.ReactNode> = {
  alert: (
    <>
      <path d="M10.3 3.6 2.6 17a1.7 1.7 0 0 0 1.5 2.6h15.8a1.7 1.7 0 0 0 1.5-2.6L13.7 3.6a1.7 1.7 0 0 0-3 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  document: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  money: (
    <>
      <rect x="2.5" y="6" width="19" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.4" />
      <path d="M6 12h.01M18 12h.01" />
    </>
  ),
  milestone: (
    <>
      <path d="M5 21V4M5 4h11l-2 3.5L16 11H5" />
    </>
  ),
  sales: (
    <>
      <path d="M3 17 9 11l4 4 8-8" />
      <path d="M15 7h6v6" />
    </>
  ),
  message: (
    <>
      <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.9-.9L3 20.5l1.5-4.4A8.4 8.4 0 0 1 3.6 12a8.4 8.4 0 0 1 8.4-8.4 8.4 8.4 0 0 1 9 7.9Z" />
    </>
  ),
  team: (
    <>
      <path d="M16 20v-1.8a3.6 3.6 0 0 0-3.6-3.6H6.6A3.6 3.6 0 0 0 3 18.2V20" />
      <circle cx="9.5" cy="7.5" r="3.5" />
      <path d="M21 20v-1.8a3.6 3.6 0 0 0-2.7-3.5M16.5 4.2a3.6 3.6 0 0 1 0 6.6" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3 4.5 6v5.3c0 4.4 3.1 8.4 7.5 9.7 4.4-1.3 7.5-5.3 7.5-9.7V6Z" />
      <path d="m9.3 12 1.9 1.9 3.5-3.6" />
    </>
  ),
  check: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12 2.4 2.4 4.6-4.8" />
    </>
  ),
  bell: (
    <>
      <path d="M18 8.6a6 6 0 1 0-12 0c0 6-2.4 7.7-2.4 7.7h16.8S18 14.6 18 8.6Z" />
      <path d="M13.7 20a2 2 0 0 1-3.4 0" />
    </>
  )
}

const GLYPH_BY_CATEGORY: Record<string, string> = {
  finance: "money",
  scheduling: "milestone",
  sales: "sales",
  documents: "document",
  calendar: "calendar",
  messages: "message",
  team: "team",
  compliance: "shield",
  project: "check",
  system: "bell"
}

export interface ContentIconProps {
  category?: string
  tone?: Tone
  size?: number
  /** Overrides the category glyph — an alert triangle for a severe row. */
  glyph?: keyof typeof Glyphs
  label?: string
}

export function ContentIcon({
  category = "project",
  tone = "neutral",
  size = 36,
  glyph,
  label
}: ContentIconProps) {
  const tint = TINT[tone]
  const chosen = glyph ?? GLYPH_BY_CATEGORY[category] ?? "bell"
  const inner = Math.round(size * 0.55)

  return (
    <span
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-testid="content-icon"
      data-tone={tone}
      data-glyph={chosen}
      style={{
        width: size,
        height: size,
        flex: "none",
        borderRadius: 10,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: tint.bg,
        color: tint.fg
      }}
    >
      <svg viewBox="0 0 24 24" width={inner} height={inner} {...stroke}>
        {Glyphs[chosen]}
      </svg>
    </span>
  )
}

/** The tile tint a notification level earns. Amber is the default alarm. */
export function toneForLevel(level: unknown, hasAttention: boolean): Tone {
  if (level === 4) return "critical"
  if (hasAttention || level === 3) return "warning"
  return "brand"
}
